import { useMessages } from '@/i18n'
import { messages } from './messages'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from '@teach/ui'
import { cn } from '@/lib/utils'

interface Props {
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
}

// Returns the sequence of page items to render.
// Numbers are actual page buttons; nulls represent ellipses ("…").
const buildPages = (current: number, total: number): (number | null)[] => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1)

  const pages: (number | null)[] = []
  const window: number[] = []
  for (let p = Math.max(2, current - 1); p <= Math.min(total - 1, current + 1); p++) {
    window.push(p)
  }

  pages.push(1)
  if (window[0] > 2) pages.push(null)
  pages.push(...window)
  if (window[window.length - 1] < total - 1) pages.push(null)
  pages.push(total)
  return pages
}

export const CatalogPagination = ({ currentPage, totalPages, onPageChange }: Props) => {
  const m = useMessages(messages)
  if (totalPages <= 1) return null

  const pages = buildPages(currentPage, totalPages)

  return (
    <nav className="flex items-center justify-center gap-1 mt-8" aria-label={m.a11yPagination}>
      <Button
        variant="ghost"
        size="icon"
        aria-label={m.a11yPrevPage}
        disabled={currentPage <= 1}
        onClick={() => onPageChange(currentPage - 1)}
      >
        <ChevronLeft className="size-4" />
      </Button>

      {pages.map((p, idx) =>
        p === null ? (
          <span
            key={`ellipsis-${idx}`}
            className="px-2 text-muted-foreground select-none"
            aria-hidden="true"
          >
            …
          </span>
        ) : (
          <Button
            key={p}
            variant={p === currentPage ? 'default' : 'ghost'}
            size="sm"
            aria-current={p === currentPage ? 'page' : undefined}
            onClick={() => onPageChange(p)}
            className={cn('min-w-9')}
          >
            {p}
          </Button>
        ),
      )}

      <Button
        variant="ghost"
        size="icon"
        aria-label={m.a11yNextPage}
        disabled={currentPage >= totalPages}
        onClick={() => onPageChange(currentPage + 1)}
      >
        <ChevronRight className="size-4" />
      </Button>
    </nav>
  )
}
